// eslint-disable-next-line no-restricted-imports
import { useTheme } from '@emotion/react';
// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  AlertIcon,
  Box,
  Button,
  CheckboxField,
  ChevronDownIcon,
  Flex,
  Image,
  List,
  Modal,
  Popover,
  RadioFieldGroup,
  SearchField,
  Skeleton,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import type { PopoverContextProps } from '@iheartjane/reefer';
import { Form, useForm, useFormContext } from '@iheartjane/reefer-hook-form';
import { Table } from '@iheartjane/reefer-table';
import { formatCurrency } from '@iheartjane/utils';
import capitalize from 'lodash-es/capitalize';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLocation } from 'react-router';

import {
  useFetchProducts,
  useGlobalProductSearch,
  useGlobalProductSearchFilters,
  useProductSearchFilters,
} from '@jane/business-admin/data-access';
import {
  useDebouncedTrack,
  useRetailOrgSettings,
  useStoreId,
} from '@jane/business-admin/hooks';
import type { MenuProductForProductsTable } from '@jane/business-admin/types';
import {
  EventNames,
  FilterNames,
  ModalNames,
  SearchSubjects,
  normalizePath,
  track,
} from '@jane/business-admin/util';
import { useImage } from '@jane/shared/components';
import { labelForVal } from '@jane/shared/util';

import { ConfirmWrapperWithTracking } from './ConfirmWrapperWithTracking';
import { EmptyState } from './stores/detail/products/table/EmptyStates';
import { CollapsedBorderTable } from './TableWithBorderSeparator';

const FORM_ERROR_NAME = 'product-search-modal-errors';

const FilterDropdown = ({
  capitalizeLabel = false,
  label,
  options,
  selected,
  onChange,
}: {
  capitalizeLabel?: boolean;
  label: string;
  onChange?: (changedOption: string, allSelections: string[]) => void;
  options: any[];
  selected: string[];
}) => {
  const formMethods = useFormContext();
  const { setValue, getValues } = formMethods;
  const popoverLabel = `${label}Filter`;
  const listLabel = `${label}List`;

  const existingValues = getValues(label);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setSearchValue('');
  }, [label]);

  const filteredOptions = useMemo(() => {
    if (!['brand', 'subcategory'].includes(label)) return options;
    if (!searchValue) return options;
    return options.filter((option) =>
      (typeof option === 'string' ? option : option.label || option.value || '')
        .toLowerCase()
        .includes(searchValue.toLowerCase())
    );
  }, [label, searchValue, options]);

  return (
    <Popover
      target={
        <Flex>
          <Button
            full
            variant={selected?.length === 0 ? 'tertiary' : 'tertiary-selected'}
            label={
              <Flex gap={8}>
                <Typography variant="body-semibold">
                  {capitalize(label)}
                </Typography>
                {label === 'stock' && selected?.length ? (
                  <Tag
                    label="1"
                    background="primary-light"
                    color="primary-dark"
                  />
                ) : selected?.length > 0 ? (
                  <Tag
                    label={`${selected.length}`}
                    background="primary-light"
                    color="primary-dark"
                  />
                ) : null}
              </Flex>
            }
            endIcon={<ChevronDownIcon size="sm" />}
          />
        </Flex>
      }
      label=""
    >
      {({ closePopover }: PopoverContextProps) => (
        <Popover.Content label={popoverLabel} maxHeight={250} padding={false}>
          <List label={listLabel}>
            <List.Item
              ariaLabel={label}
              key={`${label}-top`}
              px={24}
              pt={24}
              onClick={() => {
                setValue(label, label === 'stock' ? '' : [], {
                  shouldDirty: false,
                  shouldTouch: false,
                });
                closePopover();
              }}
            >
              <Typography variant="body-semibold">
                {capitalize(label)}
              </Typography>
            </List.Item>
            {['brand', 'subcategory'].includes(label) && (
              <Flex px={12} pb={8}>
                <SearchField
                  name={`${label}-search`}
                  label={`${label}-search`}
                  labelHidden
                  value={searchValue}
                  onChange={setSearchValue}
                />
              </Flex>
            )}
            {label === 'stock' ? (
              <Flex px={24}>
                <RadioFieldGroup
                  defaultChecked={
                    selected.includes('in_stock')
                      ? 'in_stock'
                      : selected.includes('out_of_stock')
                        ? 'out_of_stock'
                        : ''
                  }
                  name={label}
                  onChange={(val) => {
                    setValue(label, val, {
                      shouldDirty: false,
                      shouldTouch: false,
                    });
                  }}
                  options={options.map((item) => ({
                    id: item,
                    label: capitalize(item.replace(/_/g, ' ')),
                    value: item,
                  }))}
                />
              </Flex>
            ) : (
              filteredOptions.map((option) => (
                <List.Item
                  ariaLabel={`${label} ${option}`}
                  key={`${label}-${option}`}
                  px={24}
                  onClick={() => {
                    let newValues;

                    if (existingValues?.includes(option)) {
                      newValues = existingValues.filter(
                        (clickedValue: string) => {
                          return clickedValue !== option;
                        }
                      );
                    } else {
                      newValues = [...existingValues, option];
                    }

                    setValue(label, newValues, {
                      shouldDirty: false,
                      shouldTouch: false,
                    });

                    onChange && onChange(option, newValues);
                  }}
                >
                  <Flex flexDirection="row" gap={16}>
                    <CheckboxField
                      label={`${label}-${option}-checkbox`}
                      name={`${label}-${option}-checkbox`}
                      labelHidden
                      defaultChecked={existingValues?.includes(option)}
                      checked={existingValues?.includes(option)}
                    />
                    <Typography>
                      {label === 'weight'
                        ? labelForVal(option)
                        : capitalizeLabel
                          ? capitalize(option)
                          : option}
                    </Typography>
                  </Flex>
                </List.Item>
              ))
            )}
          </List>
        </Popover.Content>
      )}
    </Popover>
  );
};

const ProductSearchFilters = ({
  id,
  isGlobalSpecial,
}: {
  id: string;
  isGlobalSpecial?: boolean;
}) => {
  const { pathname } = useLocation();
  const formMethods = useFormContext();

  const { orgId } = useRetailOrgSettings();

  const { watch, setValue, getValues } = formMethods;

  const clearFilters = () => {
    setValue('brand', [], { shouldDirty: false, shouldTouch: false });
    setValue('category', [], { shouldDirty: false, shouldTouch: false });
    setValue('lineage', [], { shouldDirty: false, shouldTouch: false });
    setValue('search_name', '', { shouldDirty: false, shouldTouch: false });
    setValue('subcategory', [], { shouldDirty: false, shouldTouch: false });
    setValue('stock', '', { shouldDirty: false, shouldTouch: false });
    setValue('weight', [], { shouldDirty: false, shouldTouch: false });
  };

  const hasFilters = getValues([
    'brand',
    'category',
    'lineage',
    'search_name',
    'stock',
    'subcategory',
    'weight',
  ]).some((filter) => filter.length);

  const [brand, kind, lineage, stock, subtype, weight] = watch([
    'brand',
    'category',
    'lineage',
    'stock',
    'subcategory',
    'weight',
  ]);

  const useGetFilters = (kind: any) => {
    const { data: globalFilterData } = useGlobalProductSearchFilters({
      enabled: Boolean(isGlobalSpecial),
      kind,
      retail_org_id: orgId,
    });
    const { data: localFilterData } = useProductSearchFilters({
      category: kind,
      enabled: !isGlobalSpecial,
      storeId: id,
    });

    return {
      data: isGlobalSpecial ? globalFilterData : localFilterData,
    };
  };
  const { data: filterData } = useGetFilters(kind);

  const [searchFilter, setSearchFilter] = useState('');
  const brands = filterData?.filters?.brands || [];
  const categories = filterData?.filters?.categories || [];
  const subcategories =
    filterData?.filters?.subcategories.filter(
      (subcategory) => subcategory !== '(Subcategory not set)'
    ) || [];
  const lineages = filterData?.filters?.lineages || [];
  const weights = filterData?.filters?.weights || [];

  const trackFilterChange =
    (filterName: FilterNames) =>
    (changedOption: string, allSelections: string[]) => {
      const isSelection = allSelections.includes(changedOption);
      const commonProps = {
        all_selected_values: allSelections,
        filter_name: filterName,
        modal_name: ModalNames.ProductSearch,
        url: normalizePath(pathname, id),
      };

      if (isSelection) {
        track({
          event: EventNames.SelectedFilter,
          selected_value: changedOption,
          ...commonProps,
        });
      } else {
        track({
          event: EventNames.DeselectedFilter,
          deselected_value: changedOption,
          ...commonProps,
        });
      }
    };

  return (
    <Flex gap={16} flexWrap="wrap">
      <Form.SearchField
        label="Store search"
        name="search_name"
        placeholder={
          isGlobalSpecial
            ? 'Search product or POS name'
            : 'Search product name, brand, or POS name'
        }
        defaultValue={searchFilter}
        autoFocus={true}
        onChange={(val) => setSearchFilter(val.toLowerCase())}
        isDebounced={true}
        debounceDelay={750}
        width={350}
      />
      <FilterDropdown
        label="brand"
        options={brands}
        selected={brand}
        onChange={trackFilterChange(FilterNames.ProductBrand)}
      />
      <FilterDropdown
        label="category"
        options={categories}
        selected={kind}
        capitalizeLabel
        onChange={trackFilterChange(FilterNames.ProductCategory)}
      />
      <FilterDropdown
        label="subcategory"
        options={subcategories}
        selected={subtype}
        capitalizeLabel
        onChange={trackFilterChange(FilterNames.ProductSubcategory)}
      />
      <FilterDropdown
        label="lineage"
        options={lineages}
        selected={lineage}
        capitalizeLabel
        onChange={trackFilterChange(FilterNames.ProductLineage)}
      />
      <FilterDropdown
        label="stock"
        options={['in_stock', 'out_of_stock']}
        selected={stock}
        capitalizeLabel
        onChange={trackFilterChange(FilterNames.ProductInStock)}
      />
      {!isGlobalSpecial && (
        <FilterDropdown
          label="weight"
          options={weights}
          selected={weight}
          onChange={trackFilterChange(FilterNames.ProductWeight)}
        />
      )}
      {hasFilters ? (
        <Button
          label="Clear filters"
          onClick={clearFilters}
          variant="minimal"
        />
      ) : null}
    </Flex>
  );
};

const PriceWithStock = ({
  label,
  outOfStock,
}: {
  label: string;
  outOfStock: boolean;
}) => (
  <Flex gap={4} alignItems="center">
    <Typography>{label}</Typography>
    {outOfStock && label ? (
      <span title="Out of stock">
        <AlertIcon color="error" size="md" altText="Out of stock" />
      </span>
    ) : null}
  </Flex>
);

const TableRow = ({
  menuProduct,
  onSelectionChange,
  onlyShowInStockProducts,
  selectedProducts,
}: {
  menuProduct: MenuProductForProductsTable;
  onlyShowInStockProducts: boolean;
  onSelectionChange: (product: any, value: boolean) => void;
  selectedProducts: ProductSearchSelection[];
}) => {
  const { imageSrc } = useImage(menuProduct);

  const theme = useTheme();

  const productId = menuProduct.id.toString();

  const isChecked = useMemo(() => {
    return selectedProducts.some(
      ({ id }) => (id?.toString() || '') === productId.toString()
    );
  }, [JSON.stringify(selectedProducts), productId]);

  const onRowClick = useCallback(() => {
    onSelectionChange(menuProduct, !isChecked);
  }, [isChecked]);

  const formatPriceRange = (priceAttribute: string) => {
    if (
      !menuProduct.price_ranges ||
      !menuProduct.price_ranges[priceAttribute]
    ) {
      return { label: '', outOfStock: true };
    }

    const range = menuProduct.price_ranges[priceAttribute];

    if (!range.min && !range.max) {
      return { label: '', outOfStock: true };
    }

    const label =
      range.min === range.max
        ? formatCurrency(range.min ?? 0)
        : `${formatCurrency(range.min ?? 0)} - ${formatCurrency(range.max ?? 0)}`;

    return { label, outOfStock: range.out_of_stock === true };
  };

  return (
    <Table.Row onClick={onRowClick}>
      <Table.Cell>
        <Box minWidth="400px">
          <Flex gap={8} alignItems="center">
            <Form.CheckboxField
              label={menuProduct.name}
              name={productId}
              onChange={(value) => onSelectionChange(menuProduct, value)}
              onClick={(event) => event.stopPropagation()}
              labelHidden
              checked={isChecked}
            />
            <Image
              altText={menuProduct.full_name}
              src={imageSrc}
              borderRadius={theme.borderRadius.sm?.toString()}
              border={true}
              height="64px"
              width="64px"
            />
            <Box>
              <Typography overflowWrap="break-word" whiteSpace="normal">
                {menuProduct.name}
              </Typography>
            </Box>
          </Flex>
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Typography>{menuProduct.brand}</Typography>
      </Table.Cell>
      <Table.Cell>
        <Typography>{capitalize(menuProduct.category)}</Typography>
      </Table.Cell>
      <Table.Cell>
        <Typography>{capitalize(menuProduct.subcategory || '')}</Typography>
      </Table.Cell>
      <Table.Cell>
        <Typography>{capitalize(menuProduct.brand_subtype || '')}</Typography>
      </Table.Cell>
      <Table.Cell>
        <Typography>{capitalize(menuProduct.lineage || '')}</Typography>
      </Table.Cell>
      <Table.Cell>
        <PriceWithStock {...formatPriceRange('price_half_gram')} />
      </Table.Cell>
      <Table.Cell>
        <PriceWithStock {...formatPriceRange('price_gram')} />
      </Table.Cell>
      <Table.Cell>
        <PriceWithStock {...formatPriceRange('price_two_gram')} />
      </Table.Cell>
      <Table.Cell>
        <PriceWithStock {...formatPriceRange('price_eighth_ounce')} />
      </Table.Cell>
      <Table.Cell>
        <PriceWithStock {...formatPriceRange('price_quarter_ounce')} />
      </Table.Cell>
      <Table.Cell>
        <PriceWithStock {...formatPriceRange('price_half_ounce')} />
      </Table.Cell>
      <Table.Cell>
        <PriceWithStock {...formatPriceRange('price_ounce')} />
      </Table.Cell>
      <>
        {!onlyShowInStockProducts ? (
          <Table.Cell>
            {menuProduct.inventory_count === 0 ? (
              <span title="Out of stock">
                <AlertIcon color="error" altText="Out of stock" />
              </span>
            ) : null}
          </Table.Cell>
        ) : null}
      </>
    </Table.Row>
  );
};

const TableWrapper = styled.div({
  overflowY: 'auto',
  overflowX: 'scroll',
  position: 'relative' as const,
  boxSizing: 'border-box',
  '@media (min-width: 1375px)': {
    height: '90%',
  },
  '@media (min-width: 1024px) and (max-width: 1374px)': {
    height: '84%',
  },
  '@media (min-width: 710px) and (max-width: 1023px)': {
    height: '80%',
  },
  '@media (min-width: 501px) and (max-width: 709px)': {
    height: '78%',
  },
  '@media (min-width: 379px) and (max-width: 500px)': {
    height: '75%',
  },
  '@media (max-width: 378px)': {
    height: '70%',
  },
});

const TableHeader = ({
  onlyShowInStockProducts,
  productTypography,
}: {
  onlyShowInStockProducts: boolean;
  productTypography: string;
}) => {
  return (
    <Table.Head>
      <Table.Row>
        <Table.HeaderCell>
          <Box minWidth="400px">
            <Typography variant="caps-regular" color="grays-mid">
              {productTypography}
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="100px">
            <Typography variant="caps-regular" color="grays-mid">
              Brand
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="100px">
            <Typography variant="caps-regular" color="grays-mid">
              Category
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="100px">
            <Typography variant="caps-regular" color="grays-mid">
              Subcategory
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="100px">
            <Typography variant="caps-regular" color="grays-mid">
              Brand Subtype
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="100px">
            <Typography variant="caps-regular" color="grays-mid">
              Lineage
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="80px">
            <Typography variant="caps-regular" color="grays-mid">
              0.5g
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="80px">
            <Typography variant="caps-regular" color="grays-mid">
              1g
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="80px">
            <Typography variant="caps-regular" color="grays-mid">
              2g
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="80px">
            <Typography variant="caps-regular" color="grays-mid">
              3.5g
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="80px">
            <Typography variant="caps-regular" color="grays-mid">
              7g
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="80px">
            <Typography variant="caps-regular" color="grays-mid">
              14g
            </Typography>
          </Box>
        </Table.HeaderCell>
        <Table.HeaderCell>
          <Box minWidth="80px">
            <Typography variant="caps-regular" color="grays-mid">
              28g
            </Typography>
          </Box>
        </Table.HeaderCell>
        <>{!onlyShowInStockProducts ? <Table.HeaderCell /> : null}</>
      </Table.Row>
    </Table.Head>
  );
};

const SkeletonTableBody = ({
  numberOfRows = 10,
  onlyShowInStockProducts,
}: {
  numberOfRows?: number;
  onlyShowInStockProducts: boolean;
}) => {
  return (
    <Table.Body>
      {Array.from({ length: numberOfRows }).map((_, i) => (
        <Table.Row key={`loading-${i}`}>
          <Table.Cell width="200px">
            <Flex gap={8} alignItems="center">
              <CheckboxField
                label="loading"
                name="loading"
                labelHidden
                disabled
              />
              <Skeleton.Bone width="64px" height="64px" />
            </Flex>
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="100%" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="100%" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="100%" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="100%" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="100%" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="100%" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="60px" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="60px" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="60px" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="60px" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="60px" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="60px" height="18px" />
          </Table.Cell>
          <Table.Cell>
            <Skeleton.Bone width="60px" height="18px" />
          </Table.Cell>
          <>
            {!onlyShowInStockProducts ? (
              <Table.Cell>
                <Skeleton.Bone width="100%" height="18px" />
              </Table.Cell>
            ) : null}
          </>
        </Table.Row>
      ))}
    </Table.Body>
  );
};

const SkeletonTable = (params: {
  numberOfRows?: number;
  onlyShowInStockProducts: boolean;
}) => {
  return (
    <Skeleton animate direction="column" width="100%">
      <CollapsedBorderTable>
        <Table freezeFirstColumn>
          <TableHeader
            onlyShowInStockProducts={params.onlyShowInStockProducts}
            productTypography="x Products"
          />
          <SkeletonTableBody {...params} />
        </Table>
      </CollapsedBorderTable>
    </Skeleton>
  );
};

export type ProductSearchSelection = {
  id: number;
  menu_product_id: number;
  product_id: string;
};
type ProductSearchModalUpdatedProps = {
  closeModal: () => void;
  isGlobalSpecial?: boolean;
  onlyProductsCreatedWithinDays?: number | null;
  onlyShowInStockProducts?: boolean;
  onSubmit: (selections: ProductSearchSelection[]) => void;
  rowId?: number;
  selectedProducts: ProductSearchSelection[];
};

export const ProductSearchModalUpdated = ({
  closeModal,
  isGlobalSpecial,
  onSubmit,
  onlyProductsCreatedWithinDays,
  onlyShowInStockProducts = true,
  rowId,
  selectedProducts,
}: ProductSearchModalUpdatedProps) => {
  const storeId = useStoreId();
  const { ref: loadNextRef } = useInView({
    onChange: (inView) => {
      if (inView) {
        fetchNextPage();
      }
    },
    rootMargin: '300px',
  });
  const [selections, setSelections] = useState<ProductSearchSelection[]>(
    selectedProducts || []
  );
  const [mode, setMode] = useState<'review' | 'normal'>('normal');
  const { orgId } = useRetailOrgSettings();

  const formMethods = useForm({
    defaultValues: {
      brand: [],
      category: [],
      lineage: [],
      search_name: '',
      // Modal will have in-stock filter applied by default when first opened
      stock: 'in_stock',
      subcategory: [],
      weight: [],
    },
  });
  const {
    watch,
    formState: { isDirty },
  } = formMethods;

  const [brand, kind, lineage, name, stock, subtype, weight] = watch([
    'brand',
    'category',
    'lineage',
    'search_name',
    'stock',
    'subcategory',
    'weight',
  ]);

  const useGetProducts = (selected = false) => {
    const {
      data: productsGlobal,
      isFetching: isFetchingGlobal,
      isFetchingNextPage: isFetchingNextPageGlobal,
      isSuccess: isSuccessGlobal,
      fetchNextPage: fetchNextPageGlobal,
      hasNextPage: hasNextPageGlobal,
    } = useGlobalProductSearch({
      enabled: isGlobalSpecial,
      rowId,
      brand: brand?.length > 0 ? brand.join(',') : undefined,
      kind: kind?.length > 0 ? kind.join(',') : undefined,
      product_ids: selected
        ? selections.map((selection) => selection.id)
        : undefined,
      retail_org_id: orgId,
      in_stock: stock ? (stock === 'in_stock' ? true : false) : undefined,
      lineage: lineage?.length > 0 ? lineage.join(',') : undefined,
      name: name || undefined, // flatten a name of ''
      subtype: subtype?.length > 0 ? subtype.join(',') : undefined,
      weight: weight?.length > 0 ? weight.join(',') : undefined,
      products_created_within_days: onlyProductsCreatedWithinDays ?? undefined,
      includeHidden: true,
    });

    const {
      data: productsLocal,
      isFetching: isFetchingLocal,
      isFetchingNextPage: isFetchingNextPageLocal,
      isSuccess: isSuccessLocal,
      fetchNextPage: fetchNextPageLocal,
      hasNextPage: hasNextPageLocal,
    } = useFetchProducts({
      enabled: !isGlobalSpecial,
      params: {
        brand: brand?.length > 0 ? brand.join(',') : undefined,
        category: kind?.length > 0 ? kind.join(',') : undefined,
        in_stock: stock ? (stock === 'in_stock' ? true : false) : undefined,
        ids: selected
          ? selections.map((selection) => selection.id.toString())
          : undefined,
        lineage: lineage?.length > 0 ? lineage.join(',') : undefined,
        weight: weight?.length > 0 ? weight.join(',') : undefined,
        query: name || undefined,
        subcategory: subtype?.length > 0 ? subtype.join(',') : undefined,
        products_created_within_days:
          onlyProductsCreatedWithinDays ?? undefined,
        include_out_of_stock: !onlyShowInStockProducts,
      },
      storeId,
    });

    return {
      data: isGlobalSpecial ? productsGlobal : productsLocal,
      isFetching: isGlobalSpecial ? isFetchingGlobal : isFetchingLocal,
      isFetchingNextPage: isGlobalSpecial
        ? isFetchingNextPageGlobal
        : isFetchingNextPageLocal,
      isSuccess: isGlobalSpecial ? isSuccessGlobal : isSuccessLocal,
      fetchNextPage: isGlobalSpecial ? fetchNextPageGlobal : fetchNextPageLocal,
      hasNextPage: isGlobalSpecial ? hasNextPageGlobal : hasNextPageLocal,
    };
  };

  const {
    data: searchData,
    isFetching,
    isFetchingNextPage,
    isSuccess,
    fetchNextPage,
    hasNextPage,
  } = useGetProducts(mode === 'review');

  // Track search input here as an effect hook in order to have access to `isSuccess`,
  // rather than in the nested component with the text field
  const debouncedTrack = useDebouncedTrack(1500);
  const trackSearch = (value: string) => {
    // Prevent from firing when the form initializes
    if (!isDirty && !value) return;

    debouncedTrack({
      event: EventNames.Search,
      arguments: value,
      subject: SearchSubjects.Products,
      successful: isSuccess,
    });
  };

  useEffect(() => {
    trackSearch(name);
  }, [name]);

  const selectedCount = selections.length;
  const totalCount = useMemo(
    () => searchData?.pages[0].meta.total,
    [JSON.stringify(searchData)]
  );

  const products = useMemo(
    () => searchData?.pages.flatMap((page) => page.products) ?? [],
    [searchData]
  );

  const isTogglingAll = useRef(false);

  const handleSelectionChange = (product: any, value: boolean) => {
    const alreadyFound = selections.find(
      ({ id }) => product.id.toString() === id.toString()
    );
    // TODO: Not sure why but it tries to add things twice when we do select all, suspect it's because of the onChange
    // on the checkbox being fired, but can't find a better solution than this
    if (value === true && alreadyFound) {
      return;
    }
    setSelections((currentState) => {
      const newState = value
        ? [...currentState, product]
        : currentState.filter(
            (productInState) =>
              productInState.id.toString() !== product.id.toString()
          );

      return newState;
    });

    if (!isTogglingAll.current) {
      track({
        event: value
          ? EventNames.SelectedCheckbox
          : EventNames.DeselectedCheckbox,
        checkbox_label: product.name,
        modal_name: ModalNames.ProductSearch,
      });
    }
  };

  useEffect(() => {
    isTogglingAll.current = false;
  }, [selections]);

  const handleSubmit = () => {
    onSubmit(selections);

    if (mode === 'review') {
      setMode('normal');
    }
    closeModal();
  };

  const onSelectAll = useCallback(() => {
    const all_product_ids = searchData?.pages[0].meta.all_product_ids || [];

    setSelections(all_product_ids);
  }, [JSON.stringify(searchData)]);

  const clearSelections = () => {
    setSelections([]);
  };

  let modeSwitchLabel = `Review Selected (${selectedCount})`;
  let nextMode: 'review' | 'normal' = 'review';

  if (mode === 'review') {
    modeSwitchLabel = 'Select more';
    nextMode = 'normal';
  } else if (mode === 'normal') {
    modeSwitchLabel = `Review Selected (${selectedCount})`;
    nextMode = 'review';
  }

  useEffect(() => {
    if (mode === 'review' && selections.length === 0) {
      setMode('normal');
    }
  }, [mode, selections.length]);

  const shouldRenderModeSwitch =
    useFlag(
      isGlobalSpecial
        ? FLAGS.cnGlobalProductReviewFlow
        : FLAGS.cnStoreBasedProductReviewFlow
    ) && selectedCount > 0;

  return (
    <ConfirmWrapperWithTracking
      open
      setOpen={closeModal}
      variant="flex"
      hasChanges={isDirty}
      modalName={ModalNames.ProductSearch}
    >
      <Form.BaseForm
        name="product search modal"
        onSubmit={handleSubmit}
        formMethods={formMethods}
        formErrorName={FORM_ERROR_NAME}
      >
        <Modal.Header
          title="Select products"
          actions={[
            shouldRenderModeSwitch && (
              <Button
                variant="secondary"
                label={modeSwitchLabel}
                ml={16}
                onClick={() => setMode(nextMode)}
              />
            ),
            <Form.SubmitButton
              key="save-button"
              variant="primary"
              label="Save"
              ml={16}
            />,
          ]}
        />
        <Modal.Content overflowY="unset">
          <Form.ErrorBanner name={FORM_ERROR_NAME} />
          <ProductSearchFilters
            isGlobalSpecial={isGlobalSpecial}
            id={storeId}
          />
          <TableWrapper>
            {!(isFetching && !isFetchingNextPage) ? (
              isSuccess && products.length === 0 ? (
                <Box mt={24}>
                  <EmptyState variant="noProductsFound" />
                </Box>
              ) : (
                <div>
                  <CollapsedBorderTable>
                    <Table freezeFirstColumn>
                      <TableHeader
                        onlyShowInStockProducts={onlyShowInStockProducts}
                        productTypography={`${searchData?.pages[0].meta.total} Products`}
                      />
                      <Table.Body>
                        {products.map((product) => (
                          <TableRow
                            onlyShowInStockProducts={onlyShowInStockProducts}
                            key={product.id.toString()}
                            menuProduct={product}
                            selectedProducts={selections}
                            onSelectionChange={handleSelectionChange}
                          />
                        ))}
                      </Table.Body>
                    </Table>
                  </CollapsedBorderTable>
                  {!(isFetching && !isFetchingNextPage) ? (
                    <>
                      <div
                        id="loading-thingy"
                        ref={loadNextRef}
                        // Starts loading earlier than the bottom so user will not get stuck while scrolling
                        style={{ marginTop: '-1500px' }}
                      ></div>
                      {mode !== 'review' && hasNextPage && (
                        <CollapsedBorderTable style={{ marginTop: '1500px' }}>
                          <Table freezeFirstColumn>
                            <SkeletonTableBody
                              onlyShowInStockProducts={onlyShowInStockProducts}
                              numberOfRows={3}
                            />
                          </Table>
                        </CollapsedBorderTable>
                      )}
                    </>
                  ) : null}
                </div>
              )
            ) : (
              <SkeletonTable
                onlyShowInStockProducts={onlyShowInStockProducts}
              />
            )}
          </TableWrapper>
        </Modal.Content>
        <Modal.Footer style={{ background: 'white' }}>
          <Flex justifyContent="space-between" alignItems="center" mt={4}>
            <Typography>{`${selectedCount} products selected`}</Typography>
            <Flex gap={16}>
              {!isGlobalSpecial && (
                <Button
                  variant="tertiary"
                  label="Select all"
                  onClick={onSelectAll}
                  disabled={selectedCount === totalCount}
                />
              )}
              <Button
                variant="tertiary"
                label="Clear all selected"
                disabled={!selectedCount}
                onClick={clearSelections}
              />
            </Flex>
          </Flex>
        </Modal.Footer>
      </Form.BaseForm>
    </ConfirmWrapperWithTracking>
  );
};
